import { AttributeReference } from '@gonfalon/contexts/attributes/AttributeReference';
import type { FieldOptions } from '@gonfalon/dataviz-experimental/types';
import { msg } from '@gonfalon/messages/msg';
import type { getContextAttributeNames } from '@gonfalon/openapi/operations/getContextAttributeNames';

import { CONTEXT_ATTRS_PREFIX } from './metricEventFilterFields';

export type ContextAttributeNamesResponse = NonNullable<Awaited<ReturnType<typeof getContextAttributeNames>>['data']>;

export type ContextAttributeValueParams = { kind: string; attributeName: string };

export type ContextAttributeNameOptions = {
  options: FieldOptions[];
  valueParamsByField: Map<string, ContextAttributeValueParams>;
};

/** AttributeReference paths (`/address/city`) → dot paths (`address.city`). */
export function attributeReferenceToDotPath(reference: AttributeReference): string {
  const parts: string[] = [];
  for (let index = 0; index < reference.depth; index += 1) {
    parts.push(reference.getComponent(index));
  }
  return parts.join('.');
}

function contextAttributeNameToOption(kind: string, name: string): FieldOptions {
  const field = `${CONTEXT_ATTRS_PREFIX}${kind}.${name}`;
  return {
    field,
    displayValue: field.slice(CONTEXT_ATTRS_PREFIX.length),
    fieldCategory: msg('metrics.eventFilters.contextAttributesCategory'),
    allowedOperators: ['=', '!='],
    sampleValues: [],
    allowCustom: true,
  };
}

export function combineContextAttributeNameOptions(
  results: Array<{ data?: ContextAttributeNamesResponse }>,
  limit: number,
): ContextAttributeNameOptions {
  const seen = new Set<string>();
  const attributes: Array<{ option: FieldOptions; weight: number; kind: string; attributeName: string }> = [];
  for (const result of results) {
    for (const item of result.data?.items ?? []) {
      for (const attr of item.names ?? []) {
        const reference = new AttributeReference(attr.name);
        if (!reference.isValid) {
          continue;
        }
        const name = attributeReferenceToDotPath(reference);
        if (!name) {
          continue;
        }
        const field = `${CONTEXT_ATTRS_PREFIX}${item.kind}.${name}`;
        if (seen.has(field)) {
          continue;
        }
        seen.add(field);
        attributes.push({
          option: contextAttributeNameToOption(item.kind, name),
          weight: attr.weight ?? 0,
          kind: item.kind,
          // toString() yields bare names or pointers (`/address/city`) for getContextAttributeValues.
          attributeName: reference.toString(),
        });
      }
    }
  }
  const top = attributes.sort((a, b) => b.weight - a.weight).slice(0, limit);
  return {
    options: top.map((entry) => entry.option),
    valueParamsByField: new Map(
      top.map((entry) => [entry.option.field, { kind: entry.kind, attributeName: entry.attributeName }]),
    ),
  };
}
